<script setup>
// Sugestão da Central dentro da conversa (D45, D46 e D47; mockup aprovado
// design-panels/central-ia-r1/sugestao-no-chat.html). Cada item pendente da
// conversa vira uma seção no fim do chat com os balões que vão ser enviados
// (borda tracejada, "Vai ser enviada"): Enviar, Editar no próprio balão e
// Descartar. Vale para retomada, imóvel novo e comentário que virou conversa
// (resposta pública e Direct), nos dois formatos da caixa (campo ou nota
// privada: com nota, ela fica só como aviso e o balão aparece aqui).
//
// Some sozinho quando alguém da equipe responde pela conversa (D49: só
// resposta humana; IA autônoma, bot e automação não derrubam) e quando o item
// muda em outro lugar (evento ai_center.item.updated). Na retomada, quando o
// cliente escreve de novo, o servidor derruba o item (customer_replied) e o
// refaz alguns minutos depois: aqui fica o aviso "Refazendo a sugestão".
// O cartão do imóvel só existe na conta com o módulo imobiliário (D47).
//
// Convivência com a sugestão do copiloto (D50): as duas podem aparecer juntas,
// cada uma com o próprio rótulo e cor (esta em violeta, "Sugestão da Central",
// logo acima do editor; a do copiloto em verde, "Sugestão da IA", acima desta).
// Enviar por aqui avisa o copiloto (AI_CENTER_SUGGESTION_SENT) para ele tirar
// a sugestão que respondia a mesma mensagem; enviar pelo copiloto é resposta
// humana e derruba retomada e imóvel pela regra da D49. Cada balão tem o
// Reportar do copiloto (mesmo AiReportModal, origem ai_center).
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { vAutoGrow } from 'dashboard/helper/autoGrowTextarea';
import { useRoute } from 'vue-router';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import AiCenterAPI from 'dashboard/api/aiCenter';
import AiReportModal from './AiReportModal.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { isAiCenterModuleEnabled } from 'dashboard/routes/dashboard/aiCenter/aiCenterModules';
import { toIntlLocale } from 'shared/helpers/intlLocale';

const props = defineProps({
  // Número da conversa na URL (display_id).
  conversationId: { type: Number, required: true },
});

const { t, locale } = useI18n();
const route = useRoute();
const { isFeatureFlagEnabled } = usePolicy();

const FOLLOWUP_KINDS = ['opportunity', 'property'];
const OPEN_BY_KIND = {
  opportunity: ['pending', 'prepared'],
  property: ['pending', 'prepared'],
  social: ['review'],
};
// Aba da Central e status pedidos para cada tipo de item.
const TABS = [
  { tab: 'opportunities', status: 'pending,prepared' },
  { tab: 'properties', status: 'pending,prepared' },
  { tab: 'social', status: 'review' },
];
const ERROR_KEYS = {
  conversation_changed: 'CONVERSATION_CHANGED',
  window_closed: 'WINDOW_CLOSED',
  not_open: 'NOT_OPEN',
  message_blank: 'MESSAGE_BLANK',
};
// Depois destas respostas o item mudou no servidor: recarrega a conversa.
const RELOAD_ERRORS = ['conversation_changed', 'not_open'];

const items = ref([]);
const editing = reactive({});
const texts = reactive({});
const busy = reactive({});
const errors = reactive({});
// Retomada derrubada porque o cliente escreveu de novo (D49): o servidor
// refaz em alguns minutos; até lá fica o aviso no lugar do balão.
const redoing = ref(false);
// Report aberto: guarda o item e o texto da hora do clique, para o modal não
// mudar (nem fechar) se o item for atualizado ou sair da conversa.
const reporting = ref(null);
let token = 0;
let sending = 0;

const accountId = () => route.params.accountId;
const enabledTabs = computed(() =>
  TABS.filter(({ tab }) => isAiCenterModuleEnabled(tab, isFeatureFlagEnabled))
);
const realEstate = computed(() =>
  Boolean(isFeatureFlagEnabled(FEATURE_FLAGS.REAL_ESTATE_MODULE))
);

const humanRequired = item =>
  item.payload?.classification?.human_required === true;
const directUnavailable = item =>
  item.payload?.eligibility?.direct?.allowed !== true;

// Balões de um item: um por destino. Comentário com sofrimento é só Direct;
// perfil sem Direct fica só com a resposta pública.
const bubblesFor = item => {
  if (item.kind !== 'social') return [{ key: 'message', channel: null }];
  const list = [];
  const commentAllowed = item.payload?.eligibility?.comment?.allowed === true;
  if (!humanRequired(item) && commentAllowed && item.draft?.comment) {
    list.push({ key: 'comment', channel: 'CHANNEL_COMMENT' });
  }
  if (!directUnavailable(item) && item.draft?.direct) {
    list.push({ key: 'direct', channel: 'CHANNEL_DIRECT' });
  }
  return list;
};

const isChatItem = item => {
  if (!OPEN_BY_KIND[item?.kind]?.includes(item.status)) return false;
  if (item.kind === 'social') return bubblesFor(item).length > 0;
  return item.payload?.source !== 'ad_window' && Boolean(item.draft?.message);
};

const resetText = item => {
  texts[item.id] = Object.fromEntries(
    bubblesFor(item).map(({ key }) => [key, item.draft?.[key] || ''])
  );
};

const setItems = list => {
  items.value = list;
  if (list.some(item => item.kind === 'opportunity')) redoing.value = false;
  list.forEach(item => {
    if (!editing[item.id]) resetText(item);
  });
};

const load = async () => {
  token += 1;
  const current = token;
  if (!enabledTabs.value.length || !props.conversationId) {
    items.value = [];
    return;
  }
  try {
    const responses = await Promise.all(
      enabledTabs.value.map(({ tab, status }) =>
        AiCenterAPI.getItems(accountId(), {
          tab,
          conversation_display_id: props.conversationId,
          status,
          per_page: 10,
        })
      )
    );
    if (current !== token) return;
    setItems(
      responses
        .flatMap(response => response.data?.data || [])
        .filter(isChatItem)
    );
  } catch {
    // A sugestão é complementar: sem a Central, a conversa segue normal.
  }
};

const removeItem = id => {
  items.value = items.value.filter(item => item.id !== id);
  delete editing[id];
  delete errors[id];
};

const replaceItem = updated => {
  if (!updated?.id) return;
  if (!isChatItem(updated)) {
    removeItem(updated.id);
    return;
  }
  items.value = items.value.map(item =>
    item.id === updated.id ? { ...item, ...updated } : item
  );
};

const lockedBy = item =>
  item.claim && item.claim.mine === false ? item.claim.holder?.name : null;

const autoTime = item => {
  const at = Date.parse(item.payload?.auto_send_at || '');
  if (!at) return null;
  return new Intl.DateTimeFormat(toIntlLocale(locale.value || 'pt-BR'), {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(at));
};

const property = item =>
  realEstate.value && item.kind === 'property' ? item.payload?.imovel : null;
const hostOf = url => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return t('AI_CENTER.CHAT_SUGGESTION.PROPERTY_SITE');
  }
};
const network = item =>
  item.payload?.platform === 'facebook' ? 'Facebook' : 'Instagram';
const theme = item =>
  String(item.payload?.classification?.category || '').replace(/_/g, ' ');

const sendLabel = item => {
  const count = bubblesFor(item).length;
  return count > 1
    ? t('AI_CENTER.CHAT_SUGGESTION.SEND_MANY', { count })
    : t('AI_CENTER.CHAT_SUGGESTION.SEND_ONE');
};

const handleError = async (item, error) => {
  const status = error?.response?.status;
  const data = error?.response?.data || {};
  if (status === 409 && data.error === 'claimed') {
    errors[item.id] = t('AI_CENTER.CHAT_SUGGESTION.CLAIMED', {
      name: data.holder?.name || '',
    });
    return;
  }
  if (status === 409) {
    errors[item.id] = t('AI_CENTER.CHAT_SUGGESTION.CONFLICT');
    editing[item.id] = false;
    await load();
    return;
  }
  const key = ERROR_KEYS[data.error];
  errors[item.id] = t(
    key ? `AI_CENTER.CHAT_SUGGESTION.${key}` : 'AI_CENTER.ACTION_ERROR'
  );
  if (RELOAD_ERRORS.includes(data.error)) {
    useAlert(errors[item.id]);
    await load();
  }
};

const draftChanged = item =>
  bubblesFor(item).some(
    ({ key }) => (texts[item.id]?.[key] || '') !== (item.draft?.[key] || '')
  );

// Guarda o texto editado (PATCH do rascunho, com a versão do item) e devolve
// o item novo; sem mudança, devolve o mesmo.
const saveDraft = async item => {
  if (!draftChanged(item)) return item;
  const response = await AiCenterAPI.updateItem(accountId(), item.id, {
    draft: { ...item.draft, ...texts[item.id] },
    version: item.version,
  });
  const updated = response.data?.data || response.data;
  replaceItem(updated);
  return updated;
};

const run = async (item, action) => {
  busy[item.id] = true;
  errors[item.id] = null;
  try {
    await action();
  } catch (error) {
    await handleError(item, error);
  } finally {
    busy[item.id] = false;
  }
};

const toggleEdit = item =>
  run(item, async () => {
    if (!editing[item.id]) {
      editing[item.id] = true;
      return;
    }
    await saveDraft(item);
    editing[item.id] = false;
  });

const send = item =>
  run(item, async () => {
    sending += 1;
    try {
      if (item.kind === 'social') {
        const current = await saveDraft(item);
        await AiCenterAPI.send(accountId(), item.id, {
          version: current.version,
          destinations: bubblesFor(item).map(({ key }) => key),
          idempotency_key: `chat-${item.id}-${current.version}`,
        });
      } else {
        await AiCenterAPI.sendText(accountId(), item.id, {
          version: item.version,
          message: texts[item.id]?.message || '',
          idempotencyKey: `chat-${item.id}-${item.version}`,
        });
      }
      removeItem(item.id);
      emitter.emit(BUS_EVENTS.AI_CENTER_SUGGESTION_SENT, {
        conversationId: props.conversationId,
      });
      useAlert(t('AI_CENTER.CHAT_SUGGESTION.SENT'));
    } finally {
      sending -= 1;
    }
  });

const discard = item =>
  run(item, async () => {
    await AiCenterAPI.dismiss(accountId(), item.id, { version: item.version });
    removeItem(item.id);
    useAlert(t('AI_CENTER.CHAT_SUGGESTION.DISCARDED'));
  });

// Texto do report: o que está no balão agora (editado ou não). Comentário com
// dois balões leva os dois, cada um com o destino.
const reportTextOf = item => {
  const bubbles = bubblesFor(item);
  const textOf = key => (texts[item.id]?.[key] || '').trim();
  if (bubbles.length === 1 && !bubbles[0].channel) return textOf('message');
  return bubbles
    .map(({ key, channel }) =>
      t('AI_CENTER.CHAT_SUGGESTION.REPORT_CHANNEL', {
        channel: t(`AI_CENTER.CHAT_SUGGESTION.${channel}`),
        text: textOf(key),
      })
    )
    .join('\n\n');
};

const openReport = item => {
  reporting.value = {
    itemId: item.id,
    text: reportTextOf(item),
    customerText:
      item.kind === 'social' ? item.payload?.comment_text || '' : '',
  };
};

// Marcas que a IA grava na mensagem dela e origens que não são gente digitando
// (mesma regra do Message#ai_center_human_reply? no servidor).
const NOT_HUMAN_MARKERS = [
  'ai_agent_generated',
  'captain_followup',
  'captain_tool_calls',
  'captain_tool_results',
  'captain_preprocessing',
  'captain_energy_bill_grounding',
  'playbook_key',
  'captain_v2_handoff',
  'captain_material_key',
  'captain_runner_failure_fallback',
  'automation_rule_id',
  'created_via_api',
  'scheduled_message_id',
  'conversation_workflow_id',
  'macro_id',
];
const isHumanReply = message => {
  const senderType = message.sender_type || message.sender?.type;
  const echo = Boolean((message.content_attributes || {}).external_echo);
  if (String(senderType || '').toLowerCase() !== 'user' && !echo) return false;
  const attributes = message.content_attributes || {};
  if (NOT_HUMAN_MARKERS.some(key => attributes[key])) return false;
  return !message.additional_attributes?.campaign_id;
};

// Resposta pública HUMANA da equipe pela conversa: retomada e imóvel saem
// daqui na hora (o servidor confirma em seguida com ai_center.item.updated).
// Cliente, IA autônoma, bot e automação não derrubam (D49). O envio feito por
// este balão já tira o item.
const onMessageCreated = message => {
  if (Number(message?.conversation_id) !== props.conversationId) return;
  if (message.private === true || Number(message.message_type) !== 1) return;
  // Qualquer resposta pública depois do cliente cancela o refazer no servidor.
  redoing.value = false;
  if (sending > 0 || !isHumanReply(message)) return;
  const replied = items.value.filter(item =>
    FOLLOWUP_KINDS.includes(item.kind)
  );
  if (!replied.length) return;
  replied.forEach(item => removeItem(item.id));
  useAlert(t('AI_CENTER.CHAT_SUGGESTION.REPLIED'));
};

const onItemUpdated = data => {
  if (Number(data?.conversation_id) !== props.conversationId) return;
  if (OPEN_BY_KIND[data.kind]?.includes(data.status)) {
    load();
    return;
  }
  const shown = items.value.some(item => item.id === data.id);
  removeItem(data.id);
  if (shown && data.reason === 'customer_replied') redoing.value = true;
};

onMounted(() => {
  emitter.on(BUS_EVENTS.CONVERSATION_MESSAGE_CREATED, onMessageCreated);
  emitter.on(BUS_EVENTS.AI_CENTER_ITEM_UPDATED, onItemUpdated);
});
onBeforeUnmount(() => {
  emitter.off(BUS_EVENTS.CONVERSATION_MESSAGE_CREATED, onMessageCreated);
  emitter.off(BUS_EVENTS.AI_CENTER_ITEM_UPDATED, onItemUpdated);
});

watch(
  () => [props.conversationId, enabledTabs.value.length],
  () => {
    redoing.value = false;
    Object.keys(editing).forEach(key => delete editing[key]);
    reporting.value = null;
    load();
  },
  { immediate: true }
);
</script>

<template>
  <div v-if="items.length || redoing" class="flex flex-col gap-2 mt-1.5">
    <p
      v-if="redoing && !items.some(item => item.kind === 'opportunity')"
      class="m-0 inline-flex items-center gap-1.5 text-xs text-n-violet-11"
      data-testid="ai-center-suggestion-redoing"
    >
      <span class="i-ph-sparkle-bold size-[13px]" aria-hidden="true" />
      {{ t('AI_CENTER.CHAT_SUGGESTION.REDOING') }}
    </p>
    <section
      v-for="item in items"
      :key="item.id"
      class="flex flex-col gap-2 rounded-2xl border-[1.5px] border-dashed border-n-violet-7 bg-n-violet-2 px-3 pb-3 pt-2.5"
      data-testid="ai-center-opportunity-suggestion"
      :data-item-id="item.id"
    >
      <header class="flex flex-wrap items-center gap-2">
        <span
          class="inline-flex items-center gap-1.5 text-[13px] font-medium text-n-violet-11"
        >
          <span class="i-ph-sparkle-bold size-[15px]" aria-hidden="true" />
          {{ t('AI_CENTER.CHAT_SUGGESTION.TITLE') }}
        </span>
        <span class="text-xs text-n-slate-11 ltr:ml-auto rtl:mr-auto">
          {{ t('AI_CENTER.CHAT_SUGGESTION.MODE') }}
        </span>
      </header>
      <!-- Motivo em no máximo 2 linhas: quem manda é o balão abaixo. O texto
           inteiro fica no title e na página do item na Central. -->
      <p
        class="m-0 line-clamp-2 break-words text-[13px] text-n-slate-11"
        :title="item.reason_summary || undefined"
        data-testid="ai-center-suggestion-reason"
      >
        {{ item.reason_summary || t('AI_CENTER.NO_REASON') }}
      </p>

      <template v-if="item.kind === 'social'">
        <p
          class="m-0 text-xs text-n-slate-11"
          data-testid="ai-center-suggestion-origin"
        >
          {{
            theme(item)
              ? t('AI_CENTER.CHAT_SUGGESTION.ORIGIN_COMMENT_THEME', {
                  network: network(item),
                  theme: theme(item),
                })
              : t('AI_CENTER.CHAT_SUGGESTION.ORIGIN_COMMENT', {
                  network: network(item),
                })
          }}
        </p>
        <a
          v-if="item.post"
          :href="item.post.permalink || undefined"
          target="_blank"
          rel="noopener noreferrer"
          class="flex gap-2.5 rounded-[14px] border border-n-weak bg-n-solid-2 p-2.5 text-n-slate-12 no-underline"
          data-testid="ai-center-suggestion-post"
        >
          <img
            v-if="item.post.thumbnail_url"
            :src="item.post.thumbnail_url"
            alt=""
            class="size-16 flex-none rounded-[10px] object-cover"
          />
          <span
            v-else
            class="grid size-16 flex-none place-items-center rounded-[10px] bg-n-slate-4 text-n-slate-11"
          >
            <span class="i-ph-play-fill size-[22px]" aria-hidden="true" />
          </span>
          <span class="min-w-0">
            <b class="block text-[13.5px]">
              {{ t('AI_CENTER.CHAT_SUGGESTION.POST') }}
            </b>
            <span class="mt-0.5 line-clamp-2 text-[13px] text-n-slate-11">
              {{ item.post.caption }}
            </span>
          </span>
        </a>
        <div
          v-if="item.payload?.comment_text"
          class="flex max-w-[72%] flex-col gap-0.5 self-start"
        >
          <p
            class="m-0 whitespace-pre-line break-words rounded-[18px] rounded-bl-[5px] bg-n-slate-3 px-3 py-2 text-[14.5px] text-n-slate-12"
          >
            {{ item.payload.comment_text }}
          </p>
          <small class="text-[11.5px] text-n-slate-11">
            {{
              t('AI_CENTER.CHAT_SUGGESTION.COMMENT_BY', {
                author: item.author?.username || item.author?.name || '',
              })
            }}
          </small>
        </div>
        <p
          v-if="humanRequired(item)"
          class="m-0 rounded-xl border border-n-ruby-9 bg-n-ruby-2 px-3 py-2 text-[13.5px] text-n-slate-12"
          data-testid="ai-center-suggestion-human"
        >
          <b class="text-n-ruby-11">
            {{ t('AI_CENTER.CHAT_SUGGESTION.HUMAN_TITLE') }}
          </b>
          {{ t('AI_CENTER.CHAT_SUGGESTION.HUMAN_BODY') }}
        </p>
        <p
          v-else-if="directUnavailable(item)"
          class="m-0 rounded-xl border border-n-slate-7 px-3 py-2 text-[13.5px] text-n-slate-11"
          data-testid="ai-center-suggestion-no-direct"
        >
          {{ t('AI_CENTER.CHAT_SUGGESTION.DIRECT_UNAVAILABLE') }}
        </p>
      </template>

      <template v-for="bubble in bubblesFor(item)" :key="bubble.key">
        <span
          v-if="bubble.channel"
          class="self-end text-xs font-bold text-n-slate-11"
        >
          {{ t(`AI_CENTER.CHAT_SUGGESTION.${bubble.channel}`) }}
        </span>
        <div class="flex max-w-[72%] flex-col items-end gap-[3px] self-end">
          <a
            v-if="property(item) && bubble.key === 'message'"
            :href="property(item).url || undefined"
            target="_blank"
            rel="noopener noreferrer"
            class="block w-[260px] max-w-full overflow-hidden rounded-[14px] border border-n-weak bg-n-solid-2 text-n-slate-12 no-underline"
            data-testid="ai-center-suggestion-property"
          >
            <img
              v-if="property(item).foto"
              :src="property(item).foto"
              alt=""
              class="block aspect-[1.9] w-full object-cover"
            />
            <span v-else class="block aspect-[1.9] w-full bg-n-slate-4" />
            <span class="block px-2.5 py-2 text-[13px]">
              <b class="block">{{
                property(item).titulo || property(item).codigo
              }}</b>
              <small class="text-n-slate-11">{{
                hostOf(property(item).url)
              }}</small>
            </span>
          </a>
          <textarea
            v-if="editing[item.id]"
            v-model="texts[item.id][bubble.key]"
            v-auto-grow
            rows="3"
            class="!mb-0 min-h-20 w-full min-w-[240px] resize-none rounded-[18px] rounded-br-[5px] border-[1.5px] border-solid border-n-blue-9 bg-n-surface-1 px-3 py-2 text-[14.5px] text-n-slate-12 shadow-[0_0_0_3px] shadow-n-blue-9/10 outline-none"
            :data-testid="`ai-center-suggestion-edit-${bubble.key}`"
          />
          <p
            v-else
            class="m-0 whitespace-pre-line break-words rounded-[18px] rounded-br-[5px] border-[1.5px] border-dashed border-n-blue-9 bg-n-surface-1 px-3 py-2 text-[14.5px] text-n-slate-12"
            data-testid="ai-center-suggestion-bubble"
          >
            {{ texts[item.id]?.[bubble.key] }}
          </p>
          <small class="text-[11.5px] text-n-slate-11">
            {{ t('AI_CENTER.CHAT_SUGGESTION.PENDING') }}
          </small>
        </div>
      </template>

      <p
        v-if="autoTime(item)"
        class="m-0 self-end text-xs font-semibold text-n-amber-11"
        data-testid="ai-center-opportunity-suggestion-auto"
      >
        {{ t('AI_CENTER.CHAT_SUGGESTION.AUTO_AT', { time: autoTime(item) }) }}
      </p>
      <p
        v-if="errors[item.id] || lockedBy(item)"
        class="m-0 self-end text-sm text-n-ruby-11"
        role="alert"
      >
        {{
          errors[item.id] ||
          t('AI_CENTER.CHAT_SUGGESTION.CLAIMED', { name: lockedBy(item) })
        }}
      </p>
      <div class="flex flex-wrap items-center justify-end gap-2">
        <!-- Mesmo Reportar do copiloto (ícone de bandeira, ao lado do
             Descartar, longe do Enviar). -->
        <button
          v-tooltip.bottom="t('AI_CENTER.CHAT_SUGGESTION.REPORT')"
          type="button"
          data-testid="ai-center-suggestion-report"
          class="inline-flex items-center justify-center rounded-full border border-n-weak bg-transparent px-2.5 py-1 text-n-ruby-11 hover:bg-n-ruby-9/10"
          :aria-label="t('AI_CENTER.CHAT_SUGGESTION.REPORT')"
          @click="openReport(item)"
        >
          <span class="i-ri-flag-line size-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="inline-flex min-h-8 items-center gap-1.5 rounded-[10px] border border-transparent px-3.5 text-[13.5px] font-bold text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-50"
          data-testid="ai-center-suggestion-discard"
          :disabled="busy[item.id]"
          @click="discard(item)"
        >
          {{ t('AI_CENTER.CHAT_SUGGESTION.DISCARD') }}
        </button>
        <button
          type="button"
          class="inline-flex min-h-8 items-center gap-1.5 rounded-[10px] border border-n-slate-7 bg-n-surface-1 px-3.5 text-[13.5px] font-bold text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
          data-testid="ai-center-suggestion-edit"
          :disabled="busy[item.id] || Boolean(lockedBy(item))"
          @click="toggleEdit(item)"
        >
          <span
            v-if="!editing[item.id]"
            class="i-ph-pencil-simple size-[15px]"
            aria-hidden="true"
          />
          {{
            editing[item.id]
              ? t('AI_CENTER.CHAT_SUGGESTION.DONE')
              : t('AI_CENTER.CHAT_SUGGESTION.EDIT')
          }}
        </button>
        <button
          type="button"
          class="inline-flex min-h-8 items-center gap-1.5 rounded-[10px] border border-n-slate-12 bg-n-slate-12 px-3.5 text-[13.5px] font-bold text-n-surface-1 hover:opacity-90 disabled:opacity-50"
          data-testid="ai-center-suggestion-send"
          :disabled="busy[item.id] || Boolean(lockedBy(item))"
          @click="send(item)"
        >
          <span class="i-ph-paper-plane-tilt size-[15px]" aria-hidden="true" />
          {{ sendLabel(item) }}
        </button>
      </div>
    </section>
  </div>
  <AiReportModal
    v-if="reporting"
    :show="Boolean(reporting)"
    :conversation-id="conversationId"
    source="ai_center"
    :ai-text="reporting.text"
    :customer-text="reporting.customerText"
    :ai-center-item-id="reporting.itemId"
    @close="reporting = null"
  />
</template>

<style scoped>
/* O reset do dashboard zera o estilo da borda: a seção e o balão pendentes são
   tracejados (mockup), o resto é contínuo. */
button.border,
a.border,
p.border {
  border-style: solid;
}
section.border-dashed,
p.border-dashed {
  border-style: dashed;
}
</style>
